import { type Translation } from '../../../common/interfaces';
import { type PageCmsPanel, type PageCmsPanels } from './panel-types';

const omitTypename = <T extends object>(value: T): Omit<T, '__typename'> => {
  const copy = { ...value };
  delete (copy as { __typename?: unknown }).__typename;
  return copy;
};

/*
 * Serialises one panel's settings: drops __typename (on the settings and its buttonConfig) and,
 * for image panels, replaces the imageFile object with its id (mirroring the banner's bannerFileId).
 */
const buildSettingsSavePayload = (settings: PageCmsPanel['settings']) => {
  if (settings.__typename === 'ImagePanelSettings') {
    const { imageFile, buttonConfig, ...rest } = settings;
    return omitTypename({
      ...rest,
      imageFileId: imageFile?.id ?? null,
      buttonConfig: buttonConfig ? omitTypename(buttonConfig) : buttonConfig
    });
  }

  if (settings.__typename === 'TextPanelSettings') {
    const { buttonConfig, ...rest } = settings;
    return omitTypename({ ...rest, buttonConfig: buttonConfig ? omitTypename(buttonConfig) : buttonConfig });
  }

  return omitTypename(settings);
};

/*
 * Serialises panels for the save request: drops the __typename Apollo adds at every level
 * (panel, settings, buttonConfig) and, for image panels, sends imageFileId instead of imageFile.
 */
export const buildPanelsSavePayload = (panels: PageCmsPanels) =>
  panels.map((row) =>
    row.map((panel) => omitTypename({ ...panel, settings: buildSettingsSavePayload(panel.settings) }))
  );

/*
 * Serialises translations for the save request: existing (fetched) translations keep their id,
 * while newly added ones carry an empty id and are sent with id: null.
 */
export const buildTranslationsSavePayload = (translations: Translation[]) =>
  translations.map((translation) => ({
    ...translation,
    id: translation.id ? translation.id : null
  }));
